/**
 * Viral strip — IntersectionObserver-driven video playback.
 *
 * Each phone card may contain a <video data-vs-video> element. We don't
 * autoplay all of them up front — that's wasteful (5 simultaneous decode
 * pipelines) and most browsers throttle off-screen autoplay anyway. Instead
 * we play a video only while it intersects the viewport above a threshold,
 * and pause it when scrolled away.
 *
 * Video tags are already marked muted+loop+playsinline+preload="metadata",
 * which is the right combo for autoplay to be allowed without user gesture
 * and for iOS to play inline rather than fullscreen.
 *
 * Respects prefers-reduced-motion: never autoplays for those users — they
 * see the poster image (or shade color background) instead.
 */

(function () {
  if (window.__viralStripInit) return;
  window.__viralStripInit = true;

  const init = () => {
    const videos = document.querySelectorAll('[data-vs-video]');
    if (videos.length === 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      // Leave videos paused; their poster/shade background remains visible.
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      // Old browser fallback — just attempt to play all of them.
      videos.forEach((v) => playSafely(v));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            playSafely(video);
          } else {
            video.pause();
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '0px',
      }
    );

    videos.forEach((video) => observer.observe(video));
  };

  function playSafely(video) {
    // play() returns a promise that rejects if autoplay is blocked. Swallow
    // the rejection — the poster image still shows, and if a user clicks
    // the card we could try again, but for now silence is the right call.
    const result = video.play();
    if (result && typeof result.catch === 'function') {
      result.catch(() => {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Theme editor — re-bind when the section reloads.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector('[data-vs-video]')) {
      init();
    }
  });
})();
